import {useEffect, useRef} from 'react'

const openDialogs: HTMLElement[] = []
let previousBodyOverflow = ''

/** Coordinates keyboard access and scrolling across nested dialogs. */
export default function useDialogAccessibility<T extends HTMLElement>(
  isOpen: boolean,
  onClose: () => void,
  closeDisabled = false
) {
  const dialogRef = useRef<T>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  // Capture the trigger before React applies autoFocus inside the dialog.
  if (isOpen && !dialogRef.current && typeof document !== 'undefined') {
    returnFocusRef.current = document.activeElement as HTMLElement | null
  }
  const closeRef = useRef(onClose)
  const disabledRef = useRef(closeDisabled)
  closeRef.current = onClose
  disabledRef.current = closeDisabled

  useEffect(() => {
    const dialog = dialogRef.current
    if (!isOpen || !dialog) return
    const previousFocus = returnFocusRef.current || (document.activeElement as HTMLElement | null)
    if (openDialogs.length === 0) {
      previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    openDialogs.push(dialog)

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.matches(':disabled') &&
          !element.closest('[hidden], [aria-hidden="true"]') &&
          element.getClientRects().length > 0
      )
    const focusFirst = () => {
      const preferred = dialog.querySelector<HTMLElement>('[data-dialog-autofocus]')
      ;(preferred || getFocusable()[0] || dialog).focus()
    }
    const frame = requestAnimationFrame(() => {
      if (
        openDialogs[openDialogs.length - 1] === dialog &&
        !dialog.contains(document.activeElement)
      )
        focusFirst()
    })
    const handleKeyDown = (event: KeyboardEvent) => {
      if (openDialogs[openDialogs.length - 1] !== dialog || event.defaultPrevented) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        if (!disabledRef.current) closeRef.current()
      }
      if (event.key === 'Tab') {
        const elements = getFocusable()
        const first = elements[0]
        const last = elements[elements.length - 1]
        if (!first) {
          event.preventDefault()
          dialog.focus()
        } else if (
          event.shiftKey &&
          (document.activeElement === first || !dialog.contains(document.activeElement))
        ) {
          event.preventDefault()
          last.focus()
        } else if (
          !event.shiftKey &&
          (document.activeElement === last || !dialog.contains(document.activeElement))
        ) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      const index = openDialogs.indexOf(dialog)
      if (index !== -1) openDialogs.splice(index, 1)
      if (openDialogs.length === 0) document.body.style.overflow = previousBodyOverflow
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [isOpen])

  return dialogRef
}
